/**
 * Modelo de apresentação do financeiro.
 *
 * Nada de regra de divisão aqui: isso é de `@nv/core/finance`, e o servidor
 * roda a MESMA função. Este arquivo só decide o que a tela mostra.
 */

import { formatMoney } from '@nv/core';

import type { IconName } from '../../ui/icons';
import type { ExpenseRow, FinanceSummary } from './api';

export type Escopo = 'minha' | 'familia' | 'grupo';

const TODOS_OS_ESCOPOS: { key: Escopo; label: string }[] = [
  { key: 'minha', label: 'Minha viagem' },
  { key: 'familia', label: 'Família' },
  { key: 'grupo', label: 'Grupo' },
];

/**
 * As abas disponíveis para quem está olhando.
 *
 * Sem família, a aba `Família` **não aparece** — em vez de aparecer zerada.
 * Zero significaria "a sua família não gastou nada", que é diferente de "você
 * não tem família", e a segunda é a informação verdadeira.
 */
export function escoposDisponiveis(resumo: FinanceSummary): typeof TODOS_OS_ESCOPOS {
  return TODOS_OS_ESCOPOS.filter((e) => e.key !== 'familia' || resumo.family !== null);
}

/** Mantida para quem já importava a lista completa. */
export const ESCOPOS = TODOS_OS_ESCOPOS;

/**
 * O que cada aba mostra, dito em uma linha — sem falar de privacidade, que
 * deixou de existir no Financeiro (13/09/2026, item 34): toda despesa é da
 * viagem, e quem não quer participar simplesmente não registra.
 */
export const NOTA_DO_ESCOPO: Record<Escopo, string> = {
  minha: 'Sua parte nas despesas em que você entrou.',
  familia: 'A parte da sua família nas despesas compartilhadas.',
  grupo: 'Tudo que a viagem gastou, somado.',
};

/**
 * Categorias — as do enum `expense_category`, na ordem em que aparecem nos
 * chips. `icone` é o nome em `ui/icons.tsx`; `tom` é a cor do tile (pela
 * paleta de gráfico, não pela marca).
 */
export const CATEGORIAS: Record<string, { label: string; inicial: string; icone: IconName; tom: 1 | 2 | 3 | 4 }> = {
  alimentacao: { label: 'Alimentação', inicial: 'A', icone: 'alimentacao', tom: 4 },
  mercado: { label: 'Mercado', inicial: 'M', icone: 'mercado', tom: 3 },
  transporte: { label: 'Transporte', inicial: 'T', icone: 'carro', tom: 2 },
  combustivel: { label: 'Combustível', inicial: 'C', icone: 'combustivel', tom: 2 },
  hospedagem: { label: 'Hospedagem', inicial: 'H', icone: 'hospedagem', tom: 3 },
  ingressos: { label: 'Ingressos', inicial: 'I', icone: 'ingresso', tom: 1 },
  compras: { label: 'Compras', inicial: 'C', icone: 'compras', tom: 1 },
  lazer: { label: 'Lazer', inicial: 'L', icone: 'destaque', tom: 4 },
  farmacia: { label: 'Farmácia', inicial: 'F', icone: 'aviso', tom: 3 },
  presentes: { label: 'Presentes', inicial: 'P', icone: 'presente', tom: 1 },
  outros: { label: 'Outros', inicial: 'O', icone: 'dinheiro', tom: 2 },
};

export function categoriaDe(chave: string) {
  return CATEGORIAS[chave] ?? CATEGORIAS['outros']!;
}

export function rotuloDaCategoria(chave: string): string {
  return CATEGORIAS[chave]?.label ?? 'Outros';
}

export function inicialDaCategoria(chave: string): string {
  return CATEGORIAS[chave]?.inicial ?? 'O';
}

export interface SaldoExibido {
  /** Total do escopo escolhido. */
  totalFormatado: string;
  aReceberFormatado: string;
  aPagarFormatado: string;
  /** `true` quando não há nada a acertar — muda o texto do CTA. */
  quite: boolean;
}

export function saldoParaExibicao(
  resumo: FinanceSummary,
  escopo: Escopo,
): SaldoExibido {
  const total =
    escopo === 'grupo'
      ? resumo.groupTotalMinor
      : escopo === 'familia'
        ? (resumo.family?.shareMinor ?? resumo.myShareMinor)
        : resumo.myShareMinor;

  // O saldo a receber/pagar segue o escopo: na aba da família, é o da família
  // inteira. Mostrar o saldo pessoal sob o total da família faria dois números
  // que não conversam.
  const saldo =
    escopo === 'familia'
      ? (resumo.family?.balanceMinor ?? resumo.myBalanceMinor)
      : resumo.myBalanceMinor;

  return {
    totalFormatado: formatMoney(total, resumo.currency),
    // Saldo positivo é a receber; negativo, a pagar. Mostrar os dois zerados
    // é melhor do que esconder um deles: a tela fica com a mesma forma sempre.
    aReceberFormatado: formatMoney(Math.max(saldo, 0), resumo.currency),
    aPagarFormatado: formatMoney(Math.max(-saldo, 0), resumo.currency),
    quite: saldo === 0,
  };
}

export interface LinhaDeCategoria {
  chave: string;
  label: string;
  valorFormatado: string;
  /** 0 a 100, para a barra. */
  percentual: number;
}

export function categoriasParaExibicao(
  resumo: FinanceSummary,
): LinhaDeCategoria[] {
  const maior = Math.max(...resumo.byCategory.map((c) => c.totalMinor), 1);

  return [...resumo.byCategory]
    .sort((a, b) => b.totalMinor - a.totalMinor)
    .map((c) => ({
      chave: c.category,
      label: rotuloDaCategoria(c.category),
      valorFormatado: formatMoney(c.totalMinor, resumo.currency),
      percentual: Math.round((c.totalMinor / maior) * 100),
    }));
}

/** Busca por descrição e por categoria. Sem resultado cai no estado vazio. */
export function filtrarDespesas(
  despesas: readonly ExpenseRow[],
  busca: string,
): ExpenseRow[] {
  const termo = busca.trim().toLowerCase();
  if (termo.length === 0) return [...despesas];

  return despesas.filter(
    (d) =>
      d.description.toLowerCase().includes(termo) ||
      rotuloDaCategoria(d.category).toLowerCase().includes(termo),
  );
}
